import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "../aiapi";
import { getApiKey } from "../apiKeyProvider";
import ApplicationContext from "../ApplicationContext";
import { subscribeToJob, unsubscribeFromJob, subscribeToTenant, unsubscribeFromTenant } from "../signalrConnection";

// ngrok free 터널의 브라우저 경고(interstitial) 우회 — 값은 아무거나.
// 이 헤더가 빠진 요청은 ngrok 경고 HTML을 대신 받아 CORS 에러처럼 보인다.
const NGROK_HEADERS = { "ngrok-skip-browser-warning": "true" };

// 인증 헤더 — EmailJobController에는 [ApiKey] 필터가 걸려 있어 이 헤더가 없으면
// 401(problem+json)로 거절된다. 업로드뿐 아니라 상태 폴백 GET(syncStatus)도 같은
// 컨트롤러 소속이므로 모든 백엔드 fetch에 일괄로 싣는다 (테넌트 해석에도 사용됨).
const authHeaders = () => ({ ...NGROK_HEADERS, ApiKey: getApiKey() });

interface JobStatus {
    key: string;
    jobId : string|null;
    fileName: string;
    phase: string;
    jobStatus: string|null;
    message: string;
    docs: any[];
    leaving: boolean;
    fileCount?: number | null;
    duplicate?: any | null;   // 409(중복) 응답 본문 — phase 'duplicate'일 때 카드의 안내 패널이 그린다
    skippedFiles?: any[];     // 이미 처리된 첨부(job_files.status = Duplicate) — 카드에 "Already processed · Open ↗"로 표시
    resumed?: boolean;        // 서버가 이전 job을 자동으로 이어서 처리한 경우(레코드가 전부 삭제됐거나 전부 실패)
}

/** detail 폴백의 서버 상태 → phase. 초기 단계 표기만 끌어올린다(이미 앞선 단계면 그대로).
 *  진행 중인 job을 뒤늦게 채택했을 때(중복 409 inProgress)나 Resume 직후에도 이 매핑으로 현재 단계가 바로 보인다. */
const EARLY_PHASES = ["uploading", "uploaded", "classifying", "classified"];
function phaseFromDetail(phase: string, status: string) {
  if (phase === "error") return phase;
  switch (status) {
    case "AwaitingSelection":
      return ["uploading", "uploaded", "classifying"].includes(phase) ? "classified" : phase;
    case "Processing":
      return EARLY_PHASES.includes(phase) || phase === "resuming" ? "processing" : phase;
    case "Completed":
      return "done";
    case "CompletedWithErrors":
      return "doneWithErrors";
    case "Failed":
    case "NoDocuments":
      return "failed";
    default:
      return phase;
  }
}

/**
 * AI Drag & Drop zone용 멀티 job 상태 관리 훅.
 * 파일을 떨어뜨릴 때마다 job 엔트리가 추가되고, 각 엔트리는 독립적으로
 * 업로드 → 분류 → 문서별 (Parsing → Parsed → Doing → Done) 진행을 추적한다.
 *
 * 엔트리 형태:
 * {
 *   key, jobId, fileName,
 *   phase: 'uploading'|'uploaded'|'classifying'|'classified'|'processing'
 *        | 'done'|'doneWithErrors'|'failed'|'error',
 *   jobStatus,          // backend raw status (AwaitingSelection 등)
 *   message,            // 마지막 상태 메시지 (에러 표시용)
 *   docs: [{ documentId, filename, docType, docTypeName, splitIndex,
 *            status, error, holding, actionResult }],
 *   leaving,            // 퇴장 애니메이션 중
 * }
 *
 * @param {object}   [options]
 * @param {(document, raw) => void} [options.onDocumentEvent]
 *   customMessage에 event/document가 실려 오면 호출 (raw.event === 'documentCompleted'
 *   인 경우가 토스트 대상). 문서 상태 병합은 훅이 알아서 한다.
 * @param {(duplicate) => void} [options.onDuplicate]
 *   업로드가 409(이미 처리됨 / 처리 중)로 막혔을 때 응답 본문(DuplicateResponse)으로 호출 — 토스트용.
 *   카드 표시는 훅이 phase 'duplicate' + job.duplicate로 알아서 한다.
 *
 * 중복(409) 처리 (2026-09-22):
 *   - 이미 처리됨: phase 'duplicate' — 카드에 안내·생성 데이터 링크·남은 문서를 보여 주고 업로드는 막힌다.
 *     [Resume remaining] → resumeJob(key, jobId) → POST /api/emailjob/{jobId}/reopen → 원본 job의
 *     실패·미처리 문서만 다시 돌아가고, 이 엔트리가 그 job의 진행을 추적한다.
 *   - 처리 중(inProgress): 원본 jobId를 이 엔트리가 채택해 진행을 그대로 보여 준다.
 */
export function useEmailJobStatus({ onDocumentEvent, onDuplicate, onSkippedAttachment }
  : {onDocumentEvent : any, onDuplicate?: any, onSkippedAttachment?: any}) {
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const timersRef = useRef(new Map());      // key -> timeout[] (완료 후 자동 제거용)
  const committedRef = useRef(new Set());   // 자동 커밋을 이미 보낸 jobId (중복 제출 방지)
  const subscribedRef = useRef(new Set());  // SignalR join을 마친 jobId
  const toastedSkipRef = useRef(new Set()); // 이미 토스트를 띄운 Duplicate 첨부(fileId) — detail 재조회마다 또 띄우지 않게
  const appContext = useContext(ApplicationContext);

  const handlerRef = useRef(onDocumentEvent);
  const duplicateHandlerRef = useRef(onDuplicate);
  const skippedHandlerRef = useRef(onSkippedAttachment);
  useEffect(() => {
    handlerRef.current = onDocumentEvent;   // 구독은 1회지만 콜백은 항상 최신을 부른다
    duplicateHandlerRef.current = onDuplicate;
    skippedHandlerRef.current = onSkippedAttachment;
  });

  const patchJob = useCallback((key, patch) => {
    setJobs((prev: any) =>
      prev.map((j:any) => (j.key === key ? { ...j, ...(typeof patch === "function" ? patch(j) : patch) } : j)));
  }, []);

  const clearTimers = useCallback((key) => {
    (timersRef.current.get(key) ?? []).forEach(clearTimeout);
    timersRef.current.delete(key);
  }, []);

  /** 엔트리 제거 (× 버튼 / 완료 자동 제거). 퇴장 애니메이션 후 실제 제거. */
  const removeJob = useCallback(
    (key) => {
      clearTimers(key);
      patchJob(key, { leaving: true });
      const t = setTimeout(() => {
        setJobs((prev : any) => {
          const target = prev.find((j : any) => j.key === key);
          if (target?.jobId && subscribedRef.current.has(target.jobId)) {
            unsubscribeFromJob(target.jobId);
            subscribedRef.current.delete(target.jobId);
          }
          return prev.filter((j: any) => j.key !== key);
        });
      }, 240); // 퇴장 애니메이션(200ms)과 맞춤
      timersRef.current.set(key, [t]);
    },
    [clearTimers, patchJob]
  );

  /** 모든 작업이 성공(Completed)으로 끝난 엔트리는 완료 표시 5초 후 자동으로 사라진다. */
  const scheduleAutoRemove = useCallback(
    (key) => {
      if (timersRef.current.has(key)) return; // 중복 예약 방지
      const t = setTimeout(() => removeJob(key), 5000);
      timersRef.current.set(key, [t]);
    },
    [removeJob]
  );

  // ── 자동 전체 커밋: 분류 완료(AwaitingSelection) 감지 즉시 전 문서 처리 요청 ──
  // (추후 선택 UI로 갈 때는 이 함수를 사용자의 커밋 버튼 핸들러로 바꾸면 된다 —
  //  backend selections 엔드포인트 구조는 두 모드 공용)
  const autoCommit = useCallback(
    async (key, jobId, docs) => {
      if (committedRef.current.has(jobId)) return;
      const ids = docs
        .filter((d: any) => d.status === "AwaitingSelection")
        .map((d: any) => d.documentId);
      if (ids.length === 0) return;
      committedRef.current.add(jobId);

      try {
        const res = await fetch(`${BACKEND_URL}/api/emailjob/${jobId}/selections`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            selectedDocumentIds: ids,
            userId: appContext.user?.id || null,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message ?? `HTTP ${res.status}`);
        }
        patchJob(key, { phase: "processing" });
      } catch (e :any) {
        committedRef.current.delete(jobId); // 재시도 허용
        patchJob(key, { phase: "error", message: `Failed to start processing: ${e.message ?? e}` });
      }
    },
    [patchJob, appContext]
  );

  /** detail 트리를 가져와 문서 목록을 평탄화해 반영한다. 필요 시 자동 커밋도 이어서. */
  const fetchDetail = useCallback(
    async (key, jobId) => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/emailjob/${jobId}/detail`, {
          headers: authHeaders(),
        });
        if (!res.ok) return; // 파일 등록 전(404 등)은 조용히 무시 — 이벤트가 다시 부른다
        const data = await res.json();

        const docs = (data.files ?? []).flatMap((f:any) =>
          (f.documents ?? []).map((d: any) => ({ ...d, filename: f.filename })));
        // 이미 처리된 첨부(Duplicate) — 문서가 없으므로 파일 줄로 따로 보여 준다 (이전 레코드 링크는 duplicateOf.documents)
        const skippedFiles = (data.files ?? [])
          .filter((f: any) => f.status === "Duplicate")
          .map((f: any) => ({ fileId: f.fileId, filename: f.filename, error: f.error, duplicateOf: f.duplicateOf ?? null }));
        // 처음 본 Duplicate 첨부는 토스트로도 알린다 (그 첨부가 만든 레코드마다 하나)
        skippedFiles.forEach((f: any) => {
          if (toastedSkipRef.current.has(f.fileId)) return;
          toastedSkipRef.current.add(f.fileId);
          skippedHandlerRef.current?.(f, jobId);
        });
        patchJob(key, (j: any) => {
          // 분류 결과가 왔는데 아직 초기 단계 표기라면 classified로, 처리 중이면 processing으로 올린다 (phaseFromDetail)
          const phase = phaseFromDetail(j.phase, data.status);
          return {
            jobStatus: data.status,
            docs,
            skippedFiles,
            phase,
            message: phase === "failed" && !j.message ? (data.summaryMessage ?? "") : j.message,
          };
        });

        if (data.status === "AwaitingSelection") await autoCommit(key, jobId, docs);
      } catch {
        // 네트워크 순단은 무시 — 다음 customMessage에서 재시도된다
      }
    },
    [patchJob, autoCommit]
  );

  /**
   * join 직후 1회 — join 전에 방송되어 유실됐을 수 있는 초기 상태(pass2n8n)를 따라잡는다.
   * ngrok 등으로 SignalR 연결/negotiate가 느리면 backend의 pass2n8n 통지가
   * JoinJobGroup보다 먼저 나가 유실되어, Classifying 구간이 Uploaded로 보이는 문제 보정.
   * GET /api/emailjob/{jobId} 는 인메모리 레지스트리 상태를 소문자로 돌려준다.
   * AwaitingSelection 이후 단계는 fetchDetail 폴백이 커버하므로 여기선 pass2n8n만 본다.
   */
  const syncStatus = useCallback(
    async (key, jobId) => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/emailjob/${jobId}`, {
          headers: authHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (String(data?.status ?? "").toLowerCase() === "pass2n8n") {
          handleMessageRef.current?.(key, jobId, { jobId, status: "pass2n8n", message: "" });
        }
      } catch {
        // 표시 보정용 — 실패해도 파이프라인 진행엔 영향 없음
      }
    },
    []
  );

  const handleMessage = useCallback(
    (key, jobId, data) => {
      // customMessage는 구독 중인 모든 리스너에 브로드캐스트되므로 반드시 jobId로 필터
      if (String(data.jobId) !== String(jobId)) return;

      // 문서 단위 이벤트: 목록에 병합 + (documentCompleted면) 토스트 콜백
      if (data.event && data.document) {
        const doc = data.document;
        const TERMINAL = ["Completed", "Failed", "Skipped"];
        patchJob(key, (j:any) => {
          const exists = j.docs.some((d: any) => d.documentId === doc.documentId);
          return {
            docs: exists
              ? j.docs.map((d: any) => {
                  if (d.documentId !== doc.documentId) return d;
                  // 늦게 도착한 stale 이벤트 방어: 이미 터미널(Done/Failed/Skipped)인 문서를
                  // 비터미널 상태(Standardized+holding 등)로 되돌리는 업데이트는 무시한다.
                  if (TERMINAL.includes(d.status) && doc.status && !TERMINAL.includes(doc.status))
                    return d;
                  return { ...d, ...doc };
                })
              : [...j.docs, doc],
          };
        });
        handlerRef.current?.(doc, data);
      }

      const status = data.status;
      if (!status) return;

      if (status === "pass2n8n") {
        // 부분 갱신이 기존 개수를 지우지 않게 — 값이 실제로 온 경우에만 덮어쓴다.
        // (fileCount 없이 오는 pass2n8n이 있다: 위 syncStatus의 합성 메시지)
        patchJob(key, (j: any) => ({
          phase: "classifying",
          message: data.message ?? "",
          fileCount: data.fileCount ?? j.fileCount ?? null,
        }));
      } else if (status === "error") {
        patchJob(key, { phase: "error", message: data.message ?? "Failed to analyze the email." });
      } else if (status === "AwaitingSelection") {
        patchJob(key, { phase: "classified", jobStatus: status });
        fetchDetail(key, jobId); // 분류 결과(Category/Document) 로드 → 자동 커밋
      } else if (status === "Processing") {
        patchJob(key, (j: any) => ({
          jobStatus: status,
          phase: j.phase === "error" ? j.phase : "processing",
        }));
      } else if (status === "Completed") {
        patchJob(key, { phase: "done", jobStatus: status });
        fetchDetail(key, jobId); // 최종 스냅샷(actionResult 포함) 반영
        scheduleAutoRemove(key); // ✔ 전체 Done 표시 5초 후 자동 소멸
      } else if (status === "CompletedWithErrors") {
        patchJob(key, { phase: "doneWithErrors", jobStatus: status });
        fetchDetail(key, jobId); // 실패 포함 — 사용자가 확인하고 ×로 닫도록 유지
      } else if (status === "Failed" || status === "NoDocuments") {
        patchJob(key, { phase: "failed", jobStatus: status, message: data.message ?? "" });
      }
    },
    [patchJob, fetchDetail, scheduleAutoRemove]
  );

  // syncStatus → handleMessage 순환 참조 없이 항상 최신 handleMessage를 부르기 위한 ref
  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  });

  const subscribe = useCallback(
    async (key, jobId) => {
      if (subscribedRef.current.has(jobId)) return;
      subscribedRef.current.add(jobId);
      try {
        await subscribeToJob(jobId, {
          onMessage: (data : any) => handleMessage(key, jobId, data),
        });
        // join 전에 지나간 이벤트 대비 폴백
        syncStatus(key, jobId);   // 유실된 pass2n8n 따라잡기 → Classifying 표시
        fetchDetail(key, jobId);  // AwaitingSelection 이후 단계 폴백
      } catch (e :any) {
        await unsubscribeFromJob(jobId); // handler remove
        subscribedRef.current.delete(jobId);
        patchJob(key, { phase: "error", message: `Realtime connection failed: ${e.message ?? e}` });
      }
    },
    [handleMessage, syncStatus, fetchDetail, patchJob]
  );

  /**
   * 특정 job에 종속되지 않는 tenant 전체 customMessage 구독. 이미 subscribe()로 추적 중인
   * job의 이벤트는 그쪽 리스너가 처리하므로 여기선 건너뛰어 토스트 중복을 막는다.
   * tenant 그룹은 같은 tenant의 모든 사용자가 공유하므로, 다른 사용자의 job 이벤트가
   * 섞여 오지 않도록 data.userId가 현재 로그인 사용자와 일치할 때만 처리한다.
   */
  const global_message_jobid = "d60d6a2f-166d-476e-b2e5-4adf4aa93229";
  const subscribeGlobal = useCallback(async () => {
    if(subscribedRef.current.has(global_message_jobid)) return;
    subscribedRef.current.add(global_message_jobid);
    try {
      await subscribeToTenant({
        onMessage: (data: any) => {
          if (String(data.jobId) !== global_message_jobid) return;
          // check fms userId vs current userId to avoid other users' job events
          if (String(data.userId ?? "") !== String(appContext.user?.id ?? "")) return;
          if (data.event && data.document) handlerRef.current?.(data.document, data);
        },
      });
    } catch (e: any) {
      console.error("Failed to subscribe to tenant-wide job updates:", e);
    }
  }, [appContext]);

  const unsubscribeGlobal = useCallback(() => {
    unsubscribeFromTenant();
    subscribedRef.current.delete(global_message_jobid);
  }, []);

  /**
   * 업로드 409(중복) 처리 — 서버가 같은 메일/파일을 이미 처리했거나 처리 중이라고 막은 경우.
   *  - 처리 중(inProgress): 원본 job을 이 엔트리가 대신 추적한다 (jobId 채택 → SignalR join → detail 폴백)
   *  - 이미 처리됨: phase 'duplicate' — 카드가 안내·생성된 데이터 링크·남은 문서를 그린다 (업로드는 막힘)
   */
  const adoptDuplicate = useCallback(
    async (key, dup) => {
      const jobId = dup?.original?.jobId ?? null;
      duplicateHandlerRef.current?.(dup);
      if (dup?.inProgress && jobId) {
        patchJob(key, { jobId, phase: "classifying", message: dup.message ?? "", duplicate: dup });
        await subscribe(key, jobId);   // 서버가 원본 job을 레지스트리에 올려 두므로 join이 된다
        return;
      }
      patchJob(key, { jobId, phase: "duplicate", message: dup?.message ?? "This file was already processed.", duplicate: dup });
    },
    [patchJob, subscribe]
  );

  /**
   * 중복 카드의 [Resume remaining] — 원본 job에서 실패했거나 처리되지 않은 문서만 다시 돌린다.
   * 서버(reopen)가 문서를 다시 열고 곧바로 전부 선택해 큐에 넣으므로 프론트 자동 커밋은 필요 없다.
   * 이후 진행은 이 엔트리가 그 jobId로 구독해 종전 UI(Parsing→Doing→Done)로 보여 준다.
   */
  const resumeJob = useCallback(
    async (key, jobId) => {
      if (!jobId) return;
      patchJob(key, { phase: "resuming", message: "" });
      try {
        const res = await fetch(`${BACKEND_URL}/api/emailjob/${jobId}/reopen`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ userId: appContext.user?.id || null }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message ?? `HTTP ${res.status}`);
        }
        committedRef.current.add(jobId);   // 선택은 서버가 이미 끝냈다 — autoCommit이 다시 제출하지 않게
        patchJob(key, { phase: "processing", duplicate: null });
        if (subscribedRef.current.has(jobId)) {
          // 같은 화면에서 먼저 올렸던 엔트리가 아직 이 job을 구독 중 — 리스너를 이 엔트리로 옮긴다
          unsubscribeFromJob(jobId);
          subscribedRef.current.delete(jobId);
        }
        await subscribe(key, jobId);
      } catch (e: any) {
        patchJob(key, { phase: "duplicate", message: `Resume failed: ${e.message ?? e}` });
      }
    },
    [patchJob, subscribe, appContext]
  );

  const uploadOne = useCallback(
    async (file) => {
      //const key = crypto.randomUUID();
      const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setJobs((prev) =>  [
        ...prev, // 새 항목은 카드 하단에 추가
        {
          key, jobId: null, fileName: file.name, phase: "uploading",
          jobStatus: null, message: "", docs: [], leaving: false,
        },
      ]);

      try {
        const formData = new FormData();
        formData.append("file", file);
        // 실행 사용자 — 서버가 이전 job을 자동으로 이어서 처리할 때(레코드 전부 삭제 등) 그 job에 기록된다
        if (appContext.user?.id) formData.append("userId", String(appContext.user.id));
        const { data } = await api.post("/api/emailjob", formData, {
          headers: { "ApiKey": getApiKey() },
        });

        // 서버가 이전 job을 자동으로 이어서 처리함(처리 전적은 있으나 레코드가 하나도 안 살아 있음) —
        // 그 job을 이 엔트리가 추적한다. 선택은 서버가 이미 끝냈으므로 자동 커밋은 필요 없다.
        if (data?.resumed && data?.jobId) {
          committedRef.current.add(data.jobId);
          duplicateHandlerRef.current?.({ resumed: true, message: data.message, original: { jobId: data.jobId } });
          patchJob(key, { jobId: data.jobId, phase: "processing", message: data.message ?? "", resumed: true });
          await subscribe(key, data.jobId);
          return;
        }

        // 업로드 응답 성공 = 서버가 접수했고 분류가 곧 시작된다는 확정 신호이므로
        // 곧바로 Classifying으로 표시한다. (이전에는 'uploaded'로 두고 서버의 pass2n8n
        // 푸시를 기다렸는데, 원거리 서버에서는 SignalR join 전에 그 푸시가 유실되어
        // Uploaded에 멈춰 보이는 문제가 있었다. 이후 도착하는 pass2n8n은 같은 phase라 무해.)
        patchJob(key, { jobId: data.jobId, phase: "classifying" });
        await subscribe(key, data.jobId);
      } catch (e:any) {
        // 409 = 중복(이미 처리됨 / 처리 중) — 오류가 아니라 안내 카드로 보여 준다
        const dup = e?.response?.status === 409 ? e.response.data : null;
        if (dup?.duplicate) {
          await adoptDuplicate(key, dup);
          return;
        }
        const detail = e?.response?.data?.message ?? e.message ?? e;
        patchJob(key, { phase: "error", message: `Upload failed: ${detail}` });
      }
    },
    [patchJob, subscribe, adoptDuplicate, appContext]
  );

  /** 드롭/선택된 파일들 중 .eml/.msg만 업로드한다. 반환값: 접수된 파일 수 */
  const allowedExtensions = /\.(eml|msg|jpg|jpeg|png|tif|tiff|pdf|xls|xlsx|doc|docx)$/i;
  const addFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList ?? []).filter((f: any) => allowedExtensions.test(f.name));
      files.forEach(uploadOne);
      return files.length;
    },
    [uploadOne]
  );

  /** 에러 엔트리 재시도 — 커밋 실패는 detail 재조회로 자동 커밋이 다시 돈다. */
  const retryJob = useCallback(
    (key) => {
      setJobs((prev : any) => {
        const j = prev.find((x: any) => x.key === key);
        if (j?.jobId) {
          fetchDetail(key, j.jobId);
          return prev.map((x: any) => (x.key === key ? { ...x, phase: "classified", message: "" } : x));
        }
        return prev;
      });
    },
    [fetchDetail]
  );

  // 언마운트: 타이머/구독 정리
  useEffect(() => {
    const timers = timersRef.current;
    const subscribed = subscribedRef.current;
    return () => {
      timers.forEach((list) => list.forEach(clearTimeout));
      subscribed.forEach((jobId : any) => unsubscribeFromJob(jobId));
    };
  }, []);

  return { jobs, addFiles, removeJob, retryJob, resumeJob, subscribe, subscribeGlobal, unsubscribeGlobal };
}
